// Compact language picker, shared by the public spectator view (in the hero)
// and the admin app (in the AppBar). Renders as a small frosted button showing
// the active language's endonym; tapping it opens a menu of every available
// locale. Each option is labelled in its own script so a user recognises their
// language without reading English. Picking one is an explicit selection: it
// persists and overrides the company default / device locale (see
// `I18nContext`).

import { useState, type MouseEvent } from 'react';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import LanguageIcon from '@mui/icons-material/Language';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CheckIcon from '@mui/icons-material/Check';
import { useTranslation } from '../state/I18nContext';
import { LOCALES, LOCALE_META, type Locale } from '../i18n/messages';

export function LanguageSelector() {
  const { locale, setLocale, t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleOpen = (event: MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const choose = (next: Locale) => {
    setLocale(next);
    handleClose();
  };

  return (
    <>
      <Button
        onClick={handleOpen}
        startIcon={<LanguageIcon sx={{ fontSize: 18 }} />}
        endIcon={
          <KeyboardArrowDownIcon
            sx={{
              fontSize: 18,
              opacity: 0.7,
              transition: 'transform .2s ease',
              transform: open ? 'rotate(180deg)' : 'none',
            }}
          />
        }
        aria-label={t('language.label')}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        sx={{
          color: 'text.primary',
          textTransform: 'none',
          fontWeight: 700,
          fontSize: 13,
          lineHeight: 1,
          minWidth: 0,
          flexShrink: 0,
          whiteSpace: 'nowrap',
          height: 38,
          pl: 1.25,
          pr: 0.75,
          gap: 0.25,
          bgcolor: 'rgba(255,255,255,0.05)',
          border: '1px solid rgba(255,255,255,0.14)',
          borderRadius: 999,
          backdropFilter: 'blur(6px)',
          '& .MuiButton-startIcon': { mr: 0.5, color: 'primary.main' },
          '& .MuiButton-endIcon': { ml: 0 },
          '&:hover': {
            bgcolor: 'rgba(255,255,255,0.09)',
            borderColor: 'rgba(244,63,94,0.5)',
          },
        }}
      >
        {LOCALE_META[locale].label}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: { mt: 1, minWidth: 160, borderRadius: 2 },
          },
        }}
      >
        {LOCALES.map((code) => (
          <MenuItem
            key={code}
            selected={code === locale}
            onClick={() => choose(code)}
            sx={{ fontWeight: code === locale ? 700 : 500, py: 1 }}
          >
            {code === locale && (
              <ListItemIcon>
                <CheckIcon fontSize="small" color="primary" />
              </ListItemIcon>
            )}
            <ListItemText inset={code !== locale}>{LOCALE_META[code].label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
